import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import { useAuthStore } from '@/features/auth/auth-store';

import {
  fetchKnowledgeStatus,
  knowledgeStatusStreamUrl,
  parseKnowledgeStatusEvent,
  type KnowledgeStatusEntry,
} from './knowledge-status-api';

/**
 * Live Knowledge Store index status for the Vault page (SCRUM-495).
 *
 * Snapshot-then-subscribe over SSE — NEVER polling (hard rule, 2026-07-10):
 * the snapshot query fills the react-query cache once; a native EventSource
 * subscription then applies each `index-status` transition to the cached map
 * via setQueryData, so every chip re-renders the moment the backend
 * broadcasts. EventSource auto-reconnects on connection loss; on any error we
 * also invalidate the snapshot so the post-reconnect state converges even if
 * transitions were missed while disconnected (the documented client
 * contract). The subscription lives for the mounting page's lifetime.
 */
const KNOWLEDGE_STATUS_KEY = ['vault', 'knowledge-status'] as const;

export function useKnowledgeStatus(): {
  statusOf: (documentId: string) => KnowledgeStatusEntry | undefined;
} {
  const queryClient = useQueryClient();
  // SCRUM-643: the SSE stream is token-less (EventSource can't carry the Bearer),
  // so it passes the authenticated tenant as a query param.
  const tenantId = useAuthStore((s) => s.user?.tenantId);
  const { data } = useQuery<Record<string, KnowledgeStatusEntry>>({
    queryKey: KNOWLEDGE_STATUS_KEY,
    queryFn: fetchKnowledgeStatus,
    staleTime: Infinity, // SSE keeps it current — time-based refetch would be polling.
    retry: false,
  });

  useEffect(() => {
    // No authenticated tenant → no stream (the guard also prevents a demo-pinned
    // fallback). Re-subscribes if the tenant changes.
    if (!tenantId) return;
    const source = new EventSource(knowledgeStatusStreamUrl(tenantId));

    source.addEventListener('index-status', (event: MessageEvent<string>) => {
      const transition = parseKnowledgeStatusEvent(event.data);
      if (!transition) return;
      queryClient.setQueryData<Record<string, KnowledgeStatusEntry>>(
        KNOWLEDGE_STATUS_KEY,
        (current) => ({
          ...(current ?? {}),
          [transition.documentId]: {
            status: transition.status,
            detail: transition.detail,
            chunksWritten: transition.chunksWritten,
            updatedAt: transition.updatedAt,
          },
        }),
      );
    });

    source.onerror = () => {
      // EventSource auto-reconnects; refetching the snapshot on error makes
      // the state converge after the gap (transitions have no replay).
      void queryClient.invalidateQueries({ queryKey: KNOWLEDGE_STATUS_KEY });
    };

    return () => {
      source.close();
    };
  }, [queryClient, tenantId]);

  return {
    statusOf: (documentId: string) => data?.[documentId],
  };
}
